import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Calendar, Upload, Plus, Trash2, Edit, Save, X, ArrowLeft, Download, FileUp, Copy, Search, Globe } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

const PublicHolidayManager = () => {
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('ALL');
  const [loadCountry, setLoadCountry] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [copyYears, setCopyYears] = useState({
    source: new Date().getFullYear(),
    target: new Date().getFullYear() + 1
  });
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    date: '',
    country: 'PL',
    description: '',
    is_bank_holiday: true
  });
  const [countries, setCountries] = useState([]);
  const [isCountryDialogOpen, setIsCountryDialogOpen] = useState(false);

  useEffect(() => {
    fetchHolidays();
    fetchCountries();
  }, [selectedCountry]);
  
  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${API_BASE_URL}/countries/`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const countriesData = response.data || [];
      console.log('Fetched countries:', countriesData);
      setCountries(countriesData);
      
      // Set first country as default if loadCountry is empty
      if (!loadCountry && countriesData.length > 0) {
        setLoadCountry(countriesData[0].code);
      }
      
      // Update holidayForm country if current value doesn't exist
      if (holidayForm.country && !countriesData.find(c => c.code === holidayForm.country)) {
        setHolidayForm(prev => ({ ...prev, country: countriesData[0]?.code || '' }));
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      // Use default countries as fallback
      const defaultCountries = [
        { code: 'PL', name: 'Poland' },
        { code: 'UK', name: 'United Kingdom' },
        { code: 'DE', name: 'Germany' },
        { code: 'FI', name: 'Finland' },
        { code: 'FR', name: 'France' },
        { code: 'ES', name: 'Spain' },
        { code: 'IT', name: 'Italy' }
      ];
      console.log('Using default countries due to error');
      setCountries(defaultCountries);
      if (!loadCountry) {
        setLoadCountry('PL');
      }
    }
  };

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const params = selectedCountry === 'ALL' ? {} : { country: selectedCountry };
      const response = await axios.get(
        `${API_BASE_URL}/public-holidays/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );
      setHolidays(response.data || []);
    } catch (error) {
      console.error("Error fetching holidays:", error);
      toast.error("Failed to load holidays");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDefaultHolidays = async () => {
    if (!window.confirm(`This will load default holidays for ${loadCountry}. Continue?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/public-holidays/load-defaults/`,
        { country: loadCountry },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Holidays loaded successfully");
      fetchHolidays();
    } catch (error) {
      console.error("Error loading holidays:", error);
      toast.error(error.response?.data?.error || "Failed to load holidays");
    }
  };

  const handleAddHoliday = async () => {
    // Check if countries exist
    if (countries.length === 0) {
      toast.error("Please add at least one country first");
      return;
    }
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/public-holidays/`,
        holidayForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Holiday added successfully");
      setIsAddDialogOpen(false);
      resetForm();
      fetchHolidays();
    } catch (error) {
      console.error("Error adding holiday:", error);
      toast.error(error.response?.data?.error || "Failed to add holiday");
    }
  };

  const handleUpdateHoliday = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.patch(
        `${API_BASE_URL}/public-holidays/${editingHoliday.id}/`,
        holidayForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Holiday updated successfully");
      setEditingHoliday(null);
      resetForm();
      fetchHolidays();
    } catch (error) {
      console.error("Error updating holiday:", error);
      toast.error(error.response?.data?.error || "Failed to update holiday");
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm("Are you sure you want to delete this holiday?")) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(
        `${API_BASE_URL}/public-holidays/${id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Holiday deleted successfully");
      fetchHolidays();
    } catch (error) {
      console.error("Error deleting holiday:", error);
      toast.error(error.response?.data?.error || "Failed to delete holiday");
    }
  };

  const resetForm = () => {
    setHolidayForm({
      name: '',
      date: '',
      country: loadCountry || countries[0]?.code || '',
      description: '',
      is_bank_holiday: true
    });
  };

  const startEdit = (holiday) => {
    setEditingHoliday(holiday);
    setHolidayForm({
      name: holiday.name,
      date: holiday.date,
      country: holiday.country,
      description: holiday.description || '',
      is_bank_holiday: holiday.is_bank_holiday
    });
  };

  const groupHolidaysByYear = (holidays) => {
    const grouped = {};
    holidays.forEach(holiday => {
      const year = new Date(holiday.date).getFullYear();
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(holiday);
    });
    return grouped;
  };

  const handleDownloadTemplate = () => {
    const headers = ['name', 'date', 'country', 'description', 'is_bank_holiday'];
    const sampleData = [
      ['New Year\'s Day', '2025-01-01', 'PL', 'New Year celebration', 'true'],
      ['Easter Monday', '2025-04-21', 'PL', 'Easter Monday holiday', 'true'],
      ['Christmas Day', '2025-12-25', 'PL', 'Christmas celebration', 'true'],
    ];
    
    let csvContent = headers.join(',') + '\n';
    sampleData.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'public_holidays_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.info('Template downloaded. Fill it with your holidays and upload it back.');
  };


  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        if (!headers.includes('name') || !headers.includes('date') || !headers.includes('country')) {
          toast.error('CSV must have columns: name, date, country');
          return;
        }
        
        const holidaysToCreate = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].match(/(".*?"|[^,]+)/g).map(v => v.trim().replace(/"/g, ''));
          const holiday = {};
          headers.forEach((header, index) => {
            holiday[header] = values[index] || '';
          });
          
          if (holiday.name && holiday.date && holiday.country) {
            holidaysToCreate.push({
              name: holiday.name,
              date: holiday.date,
              country: holiday.country,
              description: holiday.description || '',
              is_bank_holiday: holiday.is_bank_holiday === 'true'
            });
          }
        }
        
        if (holidaysToCreate.length === 0) {
          toast.error('No valid holidays found in CSV');
          return;
        }
        
        const token = localStorage.getItem("access_token");
        let created = 0;
        let failed = 0;
        
        for (const holiday of holidaysToCreate) {
          try {
            await axios.post(
              `${API_BASE_URL}/public-holidays/`,
              holiday,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            created++;
          } catch (error) {
            console.error('Failed to create holiday:', holiday, error);
            failed++;
          }
        }
        
        toast.success(`Imported ${created} holidays${failed > 0 ? `, ${failed} failed` : ''}`);
        fetchHolidays();
        
      } catch (error) {
        console.error('Error parsing CSV:', error);
        toast.error('Failed to parse CSV file');
      }
    };
    
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const handleCopyToNextYear = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/public-holidays/copy-to-next-year/`,
        {
          source_year: copyYears.source,
          target_year: copyYears.target,
          country: selectedCountry === 'ALL' ? null : selectedCountry
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setIsCopyDialogOpen(false);
      fetchHolidays();
    } catch (error) {
      console.error("Error copying holidays:", error);
      toast.error(error.response?.data?.error || "Failed to copy holidays");
    }
  };


  // Filter holidays by search term
  const filteredHolidays = holidays.filter(holiday => 
    holiday.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    holiday.country_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    holiday.country_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/holiday')}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Calendar
                </Button>
                <h2 className="text-2xl font-bold text-gray-900">Public Holiday Management</h2>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  className="flex items-center gap-2"
                  title="Download CSV template"
                >
                  <Download className="h-4 w-4" />
                  Template
                </Button>
                <Button
                  onClick={() => setIsCopyDialogOpen(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                  title="Copy holidays to next year"
                >
                  <Copy className="h-4 w-4" />
                  Copy Year
                </Button>
                <Button
                  onClick={() => setIsCountryDialogOpen(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                  title="Manage countries"
                >
                  <Globe className="h-4 w-4" />
                  Countries
                </Button>
                <label className="relative cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 pointer-events-none"
                    title="Import holidays from CSV"
                  >
                    <FileUp className="h-4 w-4" />
                    Import
                  </Button>
                </label>
                <Button
                  onClick={() => setIsAddDialogOpen(true)}
                  className="flex items-center gap-2"
                  disabled={countries.length === 0}
                  title={countries.length === 0 ? "Add countries first" : "Add new holiday"}
                >
                  <Plus className="h-4 w-4" />
                  Add Holiday
                </Button>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search holidays by name or country..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-3 py-2 border rounded-md w-full"
                  />
                </div>
              </div>
              <div className="flex gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Country
                  </label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="px-3 py-2 border rounded-md w-48"
                  >
                    <option value="ALL">All Countries</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.name} ({country.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Load Defaults For
                  </label>
                  <div className="flex gap-2">
                    {countries.length > 0 ? (
                      <>
                        <select
                          value={loadCountry}
                          onChange={(e) => setLoadCountry(e.target.value)}
                          className="px-3 py-2 border rounded-md w-48"
                        >
                          {countries.map(country => (
                            <option key={country.code} value={country.code}>
                              {country.name} ({country.code})
                            </option>
                          ))}
                        </select>
                        <Button
                          onClick={handleLoadDefaultHolidays}
                          variant="outline"
                          className="flex items-center gap-2"
                        >
                          <Upload className="h-4 w-4" />
                          Load
                        </Button>
                      </>
                    ) : (
                      <div className="text-sm text-gray-500">
                        No countries defined. Click "Countries" to add some.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading holidays...</div>
            ) : countries.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">
                  No countries defined yet. Please add countries first.
                </p>
                <Button
                  onClick={() => setIsCountryDialogOpen(true)}
                  className="flex items-center gap-2 mx-auto"
                >
                  <Globe className="h-4 w-4" />
                  Add Countries
                </Button>
              </div>
            ) : (
              <div className="space-y-6 pb-8">
                {Object.entries(groupHolidaysByYear(filteredHolidays)).sort().map(([year, yearHolidays]) => (
                  <div key={year} className="border rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-3">{year}</h3>
                    <div className="space-y-2">
                      {yearHolidays.sort((a, b) => new Date(a.date) - new Date(b.date)).map(holiday => (
                        <div key={holiday.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          {editingHoliday?.id === holiday.id ? (
                            <div className="flex-1 flex items-center gap-2">
                              <input
                                type="text"
                                value={holidayForm.name}
                                onChange={(e) => setHolidayForm({...holidayForm, name: e.target.value})}
                                className="px-2 py-1 border rounded flex-1"
                              />
                              <input
                                type="date"
                                value={holidayForm.date}
                                onChange={(e) => setHolidayForm({...holidayForm, date: e.target.value})}
                                className="px-2 py-1 border rounded"
                              />
                              <Button size="sm" onClick={handleUpdateHoliday}>
                                <Save className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => {
                                setEditingHoliday(null);
                                resetForm();
                              }}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-3">
                                <Calendar className="h-4 w-4 text-gray-500" />
                                <span className="font-medium">{holiday.name}</span>
                                <span className="text-sm text-gray-500">
                                  {new Date(holiday.date).toLocaleDateString()}
                                </span>
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold">
                                  {holiday.country_code || holiday.country}
                                </span>
                                {holiday.is_bank_holiday && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                    Bank Holiday
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEdit(holiday)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleDeleteHoliday(holiday.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {filteredHolidays.length === 0 && holidays.length > 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No holidays match your search "{searchTerm}"
                  </div>
                )}
                {holidays.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">
                      No holidays found for {selectedCountry === 'ALL' ? 'any country' : selectedCountry}.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 max-w-2xl mx-auto text-left text-sm">
                      <h4 className="font-semibold mb-2">Quick Start:</h4>
                      <ol className="list-decimal list-inside space-y-1 text-gray-600">
                        <li>Select a country and click "Load" to import default holidays</li>
                        <li>Or download the "Template" CSV file</li>
                        <li>Fill it with your holidays (format: YYYY-MM-DD)</li>
                        <li>Click "Import" to upload your CSV file</li>
                      </ol>
                      <p className="mt-3 text-xs text-gray-500">
                        CSV columns: name, date, country (PL/DE/UK/FI/EU), description, is_bank_holiday (true/false)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Holiday Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Public Holiday</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Holiday Name
              </label>
              <input
                type="text"
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({...holidayForm, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="e.g., Christmas Day"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({...holidayForm, date: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <select
                value={holidayForm.country}
                onChange={(e) => setHolidayForm({...holidayForm, country: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              >
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={holidayForm.description}
                onChange={(e) => setHolidayForm({...holidayForm, description: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                rows="2"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_bank_holiday"
                checked={holidayForm.is_bank_holiday}
                onChange={(e) => setHolidayForm({...holidayForm, is_bank_holiday: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="is_bank_holiday" className="text-sm text-gray-700">
                Bank Holiday
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsAddDialogOpen(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddHoliday}>
              Add Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Year Dialog */}
      <Dialog open={isCopyDialogOpen} onOpenChange={setIsCopyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy Holidays to Another Year</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source Year
              </label>
              <select
                value={copyYears.source}
                onChange={(e) => setCopyYears({...copyYears, source: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border rounded-md"
              >
                {[2024, 2025, 2026, 2027, 2028].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Year
              </label>
              <select
                value={copyYears.target}
                onChange={(e) => setCopyYears({...copyYears, target: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border rounded-md"
              >
                {[2025, 2026, 2027, 2028, 2029].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-600">
              <p>This will copy all holidays from {copyYears.source} to {copyYears.target}.</p>
              <p className="mt-1">Existing holidays in {copyYears.target} will not be duplicated.</p>
              {selectedCountry !== 'ALL' && (
                <p className="mt-1 font-semibold">Only {selectedCountry} holidays will be copied.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCopyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCopyToNextYear}>
              Copy Holidays
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Country Management Dialog */}
      <Dialog open={isCountryDialogOpen} onOpenChange={setIsCountryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Available Countries</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              These are the countries available for public holidays. You can load default holidays for each country using the "Load" button.
            </p>
            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Holidays Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {countries.map(country => (
                    <tr key={country.code} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium">{country.code}</td>
                      <td className="px-4 py-2 text-sm">{country.name}</td>
                      <td className="px-4 py-2 text-sm text-center">{country.holidays_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCountryDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicHolidayManager;